<script lang="ts">
  import { ResponsiveVars } from '@twist/core';
  import { cx } from '@emotion/css';
  import type { PageGallery } from '$lib/sanity/schemas';
  import { toCSSVars } from '$lib/utils/cssVars';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { type ContentSourceMap, type StudioPathLike } from '@sanity/svelte-loader';
  import PageThemeProvider from '$components/Pages/PageThemeProvider.svelte';
  import Media from '$components/SanityComponents/Common/Media.svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  let brandReskinEnabled = $derived(flagValue(FEATURE_FLAGS.BRAND_RESKIN));
  interface Props {
    document: PageGallery;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  const sectionHeightMobile = Number(document.sectionHeight.mobile.replace('px', ''));
  const sectionHeightDesktop = Number(document.sectionHeight.desktop.replace('px', ''));

  const responsiveVars = new ResponsiveVars()
    .addFluid('section-height', toFluidVarInput(sectionHeightMobile, sectionHeightDesktop))
    .toCSS();
</script>

<PageThemeProvider theme={document.theme ?? {}}>
  <section>
    <div
      class={cx(responsiveVars, 'gallery')}
      class:hide-vertical-overflow={document.hideVerticalOverflow}
    >
      <div class="gallery-content">
        {#each document.galleryItems as item}
          <div
            class="gallery-media-container"
            style={toCSSVars({
              imagePositionXDesktop: item.placement.desktop.x,
              imagePositionXMobile: item.placement.mobile.x,
              imagePositionYDesktop: item.placement.desktop.y,
              imagePositionYMobile: item.placement.mobile.y,
              imageWidthDesktop: item.placement.desktop.w,
              imageWidthMobile: item.placement.mobile.w
            })}
          >
            {#if item.media}
              <div class="gallery-media">
                <Media {sourceMap} {path} document={item.media} />
              </div>
            {/if}
          </div>
        {/each}
      </div>
    </div>
  </section>
</PageThemeProvider>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  section {
    position: relative;
  }

  .gallery {
    background-color: var(--theme-general-background-color, none);
    height: var(--section-height);
    overflow-x: clip;

    &.hide-vertical-overflow {
      overflow-y: clip;
    }
  }

  .gallery-content {
    position: relative;
    margin: var(--twist-core-spacing-0) auto;
    height: 100%;
    width: 100%;

    @include at('max') {
      max-width: $max-width-page - 2 * $max-width-margin;
    }
  }

  .gallery-media-container {
    position: absolute;
    left: var(--image-position-x-mobile);
    top: var(--image-position-y-mobile);
    width: var(--image-width-mobile);

    .gallery-media {
      height: var(--image-height-mobile);
    }
  }
</style>
